import { History, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { formatDateOnly } from "@/domains/pm/utils/protocol";
import { snapshotName } from "./snapshotName";
import type { ScheduleSnapshot } from "@/domains/pm/types";

/**
 * The chip that says, unmissably, that the chart below is not the live
 * schedule. Everything in snapshot mode is read-only, and this is the only
 * thing standing between a reader and thinking they can drag a bar — so it is
 * a solid brand-red pill, not a tinted informational one.
 *
 * It sits in the view-tabs row beside "Compare with today", shaped like the
 * unsaved-changes bar it stands in for: a full-width band of its own would
 * push the timeline down for one line of text, which is what the whole round
 * of chrome work was undoing.
 *
 * That row is tight with the sidebar expanded, so the text only shows while
 * the parent `@container` is at least 480px wide; below that the pill folds to
 * the history icon (full text in its tooltip) and keeps the × — leaving the
 * snapshot must never be the thing that gets squeezed out.
 */
export function SnapshotBanner({
  snapshot,
  onExit,
}: {
  snapshot: ScheduleSnapshot;
  onExit: () => void;
}) {
  const { t, language } = useLanguage();
  const copy = t.pm.issues.gantt;
  const name = snapshotName(snapshot, language);

  return (
    <div className="flex min-w-0 items-center gap-1 rounded-full bg-[#ef4129] py-1 pl-3 pr-1 text-xs text-white">
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="flex min-w-0 items-center gap-1.5" tabIndex={0}>
            <History className="h-3.5 w-3.5 shrink-0" aria-hidden />
            <span className="sr-only">
              {copy.snapshotViewing}: {name}
            </span>
            <span aria-hidden className="hidden whitespace-nowrap font-medium @[480px]:inline">
              {copy.snapshotViewing}
            </span>
            <span aria-hidden className="hidden truncate @[480px]:inline">
              {name}
            </span>
          </span>
        </TooltipTrigger>
        <TooltipContent>
          {copy.snapshotViewing}: {name}
        </TooltipContent>
      </Tooltip>
      <Button
        variant="ghost"
        size="control"
        onClick={onExit}
        aria-label={copy.snapshotExit}
        title={copy.snapshotExit}
        className="h-7 w-7 shrink-0 rounded-full p-0 text-white hover:bg-white/20 hover:text-white"
      >
        <X className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}
